import { useState, useEffect } from 'react';
import { Lock } from 'lucide-react';
import { useConfigTab, FORM_STYLES, ConfigTimestamps, LoadingState } from './shared';

const TEMPLATE_TYPES = [
  { value: 'A6', label: 'A6 (Thermal 4×6)' },
  { value: 'A4', label: 'A4 (Standard)' },
];

function getTemplatePath(type) {
  if (type === 'A4') return 'shipment-label-templates/default/A4/untitled.html';
  return 'shipment-label-templates/default/A6/untitled.html';
}

export function ShipmentLabelTab({ merchantId, reloadKey }) {
  const { loading, saving, isNew, configValue, metadata, saveConfig } = useConfigTab(
    merchantId, 'MERCHANT_SHIPMENT_LABEL_CONFIGURATIONS', reloadKey,
  );

  const [defaultType, setDefaultType] = useState('A6');
  const [byCarrier, setByCarrier] = useState([]);
  const [byPageType, setByPageType] = useState([]);

  useEffect(() => {
    if (!configValue) return;
    const defaultMeta = configValue.default_shipment_label_template_metadata;
    const type = defaultMeta?.type ?? 'A6';
    setDefaultType(type === 'A4' ? 'A4' : 'A6');

    const carrierMap = configValue.shipment_label_template_metadata_by_carrier_partner;
    if (carrierMap && typeof carrierMap === 'object' && !Array.isArray(carrierMap)) {
      setByCarrier(Object.entries(carrierMap).map(([carrier, val]) => ({
        carrier, type: val.type ?? '', template_path: val.template_path ?? '',
      })));
    } else {
      setByCarrier([]);
    }

    const pageTypeList = configValue.shipment_label_template_metadata_by_page_type;
    if (Array.isArray(pageTypeList)) {
      setByPageType(pageTypeList.map((p) => ({ type: p.type ?? '', template_path: p.template_path ?? '' })));
    } else {
      setByPageType([]);
    }
  }, [configValue]);

  const handleSave = () => {
    const payload = {
      shipment_label_generation_strategy: 'SYNC',
      default_shipment_label_template_metadata: {
        type: defaultType,
        template_path: getTemplatePath(defaultType),
      },
    };

    if (byCarrier.length > 0) {
      const carrierMap = {};
      for (const entry of byCarrier) {
        if (entry.carrier.trim()) {
          carrierMap[entry.carrier.trim()] = { type: entry.type.trim(), template_path: entry.template_path.trim() };
        }
      }
      if (Object.keys(carrierMap).length > 0) payload.shipment_label_template_metadata_by_carrier_partner = carrierMap;
    }

    if (byPageType.length > 0) {
      const filtered = byPageType
        .filter((p) => p.type.trim() || p.template_path.trim())
        .map((p) => ({ type: p.type.trim(), template_path: p.template_path.trim() }));
      if (filtered.length > 0) payload.shipment_label_template_metadata_by_page_type = filtered;
    }

    saveConfig({ merchant_shipment_label_configurations_value: payload });
  };

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-4">
      {isNew && (
        <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs sm:text-sm text-amber-800">
          <span className="font-medium">No existing configuration</span> - select template type below and save to create.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={FORM_STYLES.sectionCard}>
          <h3 className={FORM_STYLES.sectionTitle}>Default Template</h3>
          <div>
            <label className={FORM_STYLES.label}>Template Type <span className="text-red-500">*</span></label>
            <select value={defaultType} onChange={(e) => setDefaultType(e.target.value)} className={FORM_STYLES.input}>
              {TEMPLATE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <p className="text-[11px] text-slate-400 mt-1.5">
              Path: <span className="font-mono text-slate-500">{getTemplatePath(defaultType)}</span>
            </p>
          </div>
        </div>

        <div className={FORM_STYLES.sectionCard + ' opacity-60'}>
          <h3 className={FORM_STYLES.sectionTitle}>
            <Lock className="w-3.5 h-3.5 text-slate-400" />
            Per-Carrier Templates
            <span className="ml-auto text-[10px] font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Locked</span>
          </h3>
          {byCarrier.length === 0 ? (
            <p className="text-xs text-slate-400 italic">No per-carrier overrides configured.</p>
          ) : (
            <div className="space-y-1.5">
              {byCarrier.map((entry, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100 text-xs">
                  <span className="text-slate-600 font-medium">{entry.carrier}</span>
                  <span className="text-slate-400">→ {entry.type}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={FORM_STYLES.sectionCard + ' opacity-60'}>
        <h3 className={FORM_STYLES.sectionTitle}>
          <Lock className="w-3.5 h-3.5 text-slate-400" />
          Per-Page-Type Templates
          <span className="ml-auto text-[10px] font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Locked</span>
        </h3>
        {byPageType.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No per-page-type overrides configured.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {byPageType.map((entry, i) => (
              <div key={i} className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-slate-50 rounded-lg border border-slate-100 text-xs">
                <span className="text-slate-600 font-medium">{entry.type}</span>
                <span className="text-slate-400">→ {entry.template_path}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-slate-100">
        <button type="button" onClick={handleSave} disabled={saving} className={FORM_STYLES.btnPrimary}>
          {saving
            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          }
          {saving ? 'Saving…' : 'Save Configuration'}
        </button>
        {!isNew && <ConfigTimestamps metadata={metadata} />}
      </div>
    </div>
  );
}
